import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import applicationSettings from '../infrastructure/config';
import { TOKEN_EXPIRY_THRESHOLD_SECONDS } from './constants';
import { isTokenExpiredOrExpiring } from './tokenUtils';

/**
 * Cleans up stale OIDC state entries from session storage.
 * State entries are created for each auth/renewal request and can accumulate.
 */
function clearStaleOidcState(): void {
  const nowSeconds = Date.now() / 1000; // OIDC stores timestamps in seconds
  const userKeyPrefix = 'oidc.user:';

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('oidc.') && !key.startsWith(userKeyPrefix)) {
      try {
        const value = sessionStorage.getItem(key);
        if (value) {
          const parsed = JSON.parse(value);
          // State entries have a 'created' timestamp in seconds
          if (parsed.created && nowSeconds - parsed.created > TOKEN_EXPIRY_THRESHOLD_SECONDS) {
            keysToRemove.push(key);
          }
        }
      } catch {
        // Invalid JSON - remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Hook to clear stale tokens on page load.
 * Addresses requirement: "system never loads an old token from storage when user returns"
 *
 * When a user returns to the page with a token that is expired or about to expire,
 * this clears it and forces fresh authentication instead of using the stale token.
 *
 * The OIDC library's automaticSilentRenew handles ongoing renewal during active sessions.
 */
export function useTokenManagement(): void {
  const auth = useAuth();

  useEffect(() => {
    // Clean up stale OIDC state entries that accumulate over time
    clearStaleOidcState();

    // Clear token if expired or expiring soon (within threshold)
    // This ensures user always starts with a fresh, valid token
    if (auth.user && isTokenExpiredOrExpiring(auth.user)) {
      const storageKey = `oidc.user:${applicationSettings.oidcServiceUrl}:${applicationSettings.oidcClientId}`;
      sessionStorage.removeItem(storageKey);
      auth.removeUser();
    }
  }, []);
}
